import { useState, useEffect, useRef, useCallback } from 'react';
import {
  UploadedFile,
  mapFileStatus,
  FileStatus,
} from '../components/AgentKnowledgeBaseUpload/types';
import {
  apiService,
  FileCreateRequestDto,
  FileResponseDto,
  useCampaignFileListControllerListQuery,
  useFileCreateControllerCreateMutation,
  useFileDeleteControllerDeleteMutation,
} from '@shared/services/apiService/apiService';
import { store } from '@shared/store';
import { POLLING_INTERVAL_FILE_STATUS } from '@shared/constants/polling-intervals';
import { getUserFriendlyErrorMessage } from './utils/errorHandlers';

const PDF_MIME_TYPE = 'application/pdf';
const MAX_FILE_SIZE_MB = 50;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

interface UseKnowledgeBaseFilesResult {
  files: UploadedFile[];
  isLoading: boolean;
  error: string | null;
  uploadFile: (file: File) => Promise<void>;
  removeFile: (fileId: string) => Promise<void>;
  clearError: () => void;
  successMessage: string | null;
  clearSuccess: () => void;
}

const mapFileResponseToUploadedFile = (fileDto: FileResponseDto): UploadedFile => {
  return {
    id: fileDto.id,
    name: fileDto.fileName,
    size: fileDto.size,
    status: mapFileStatus(fileDto.status),
    uploadedAt: new Date(fileDto.createdAt),
  };
};

const validateFile = (file: File): void => {
  if (file.type !== PDF_MIME_TYPE) {
    throw new Error('Only PDF files are allowed. Please select a PDF file.');
  }

  if (file.size > MAX_FILE_SIZE_BYTES) {
    throw new Error(`File is too large. Maximum file size is ${MAX_FILE_SIZE_MB}MB.`);
  }
};

const validateCampaignId = (campaignId: string | undefined): campaignId is string => {
  if (!campaignId) {
    throw new Error('Campaign ID is required. Please ensure you are on a campaign page.');
  }
  return true;
};

const useKnowledgeBaseFiles = (campaignId: string | undefined): UseKnowledgeBaseFilesResult => {
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const pollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const {
    data: campaignFiles,
    isLoading: isLoadingFiles,
    error: fetchError,
  } = useCampaignFileListControllerListQuery({ id: campaignId || '' }, { skip: !campaignId });

  const [uploadFileMutation, { isLoading: isUploading }] = useFileCreateControllerCreateMutation();
  const [deleteFileMutation, { isLoading: isDeleting }] = useFileDeleteControllerDeleteMutation();

  const hasProcessingFiles = useCallback((fileList: UploadedFile[]): boolean => {
    return fileList.some(
      (file) => file.status === FileStatus.PARSING || file.status === FileStatus.PROCESSING,
    );
  }, []);

  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  }, []);

  const pollFileStatus = useCallback(async () => {
    if (!campaignId) return;

    try {
      const result = await store.dispatch(
        apiService.endpoints.campaignFileListControllerList.initiate(
          { id: campaignId },
          { forceRefetch: true },
        ),
      );

      if ('error' in result) {
        // Silently fail during polling - don't show error toast for background updates
        // The main fetch error handler will show errors for initial load
        return;
      }

      if ('data' in result && result.data) {
        const mappedFiles = result.data.map(mapFileResponseToUploadedFile);
        setFiles(mappedFiles);

        if (!hasProcessingFiles(mappedFiles)) {
          stopPolling();
        }
      }
    } catch (err) {
      // Silently fail during polling - user will see the files they already have
      // Main fetch will handle showing errors for critical failures
    }
  }, [campaignId, hasProcessingFiles, stopPolling]);

  const startPolling = useCallback(() => {
    stopPolling();

    pollFileStatus();
    pollingIntervalRef.current = setInterval(pollFileStatus, POLLING_INTERVAL_FILE_STATUS);
  }, [pollFileStatus, stopPolling]);

  useEffect(() => {
    if (campaignFiles) {
      const mappedFiles = campaignFiles.map(mapFileResponseToUploadedFile);
      setFiles(mappedFiles);

      if (hasProcessingFiles(mappedFiles)) {
        startPolling();
      } else {
        stopPolling();
      }
    }
  }, [campaignFiles, hasProcessingFiles, startPolling, stopPolling]);

  useEffect(() => {
    if (fetchError) {
      const errorMessage = getUserFriendlyErrorMessage(fetchError, 'fetch');
      setError(errorMessage);
    }
  }, [fetchError]);

  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);

  const uploadFile = async (file: File): Promise<void> => {
    try {
      if (!validateCampaignId(campaignId)) {
        throw new Error('Campaign ID is required. Please ensure you are on a campaign page.');
      }
      validateFile(file);
    } catch (validationError) {
      const errorMessage =
        validationError instanceof Error ? validationError.message : 'Validation failed';
      setError(errorMessage);
      throw validationError;
    }

    setError(null);
    setSuccessMessage(null);

    const validCampaignId = campaignId as string;

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('fileName', file.name);
      formData.append('campaignId', validCampaignId);

      const multipartPayload = Object.assign(formData, {
        fileName: file.name,
        campaignId: validCampaignId,
      }) as FormData & FileCreateRequestDto;

      const response = await uploadFileMutation({
        fileCreateRequestDto: multipartPayload,
        ...formData,
      }).unwrap();

      const newFile = mapFileResponseToUploadedFile(response);
      setFiles((prev) => {
        const updatedFiles = [...prev, newFile];

        const statusValue = response.status as unknown;
        const backendStatus = typeof statusValue === 'string' ? statusValue.toLowerCase() : '';

        if (backendStatus === 'parsing' && !pollingIntervalRef.current) {
          startPolling();
        }

        return updatedFiles;
      });

      setSuccessMessage(`File "${file.name}" uploaded successfully`);
    } catch (err) {
      const errorMessage = getUserFriendlyErrorMessage(err, 'upload');
      setError(errorMessage);
      throw err;
    }
  };

  const removeFile = async (fileId: string): Promise<void> => {
    setError(null);
    setSuccessMessage(null);

    try {
      const fileToRemove = files.find((f) => f.id === fileId);
      await deleteFileMutation({ id: fileId }).unwrap();
      setFiles((prev) => {
        const updatedFiles = prev.filter((f) => f.id !== fileId);

        if (!hasProcessingFiles(updatedFiles)) {
          stopPolling();
        }

        return updatedFiles;
      });

      setSuccessMessage(`File "${fileToRemove?.name || 'Unknown'}" removed successfully`);
    } catch (err) {
      const errorMessage = getUserFriendlyErrorMessage(err, 'delete');
      setError(errorMessage);
      throw err;
    }
  };

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const clearSuccess = useCallback(() => {
    setSuccessMessage(null);
  }, []);

  return {
    files,
    isLoading: isLoadingFiles || isUploading || isDeleting,
    error,
    uploadFile,
    removeFile,
    clearError,
    successMessage,
    clearSuccess,
  };
};

export default useKnowledgeBaseFiles;
